import type { SessionFrequency } from '../../../types';
import {
  ensureDate,
  toISODateString,
  generateAnchoredSessionDates,
} from '../../../utils/dateHelpers';
import {
  programWeeks,
  checkinsError,
  checkinWeekError,
} from '../../../utils/checkinWeeks';
import type { CheckinSettingsForm } from '../components/CheckinSettingsFields';
import type { FormValues } from './types';

// MED-1072 - On create, auto-populate any BLANK coaching session dates
// from the session 1 (anchor) date, using the configured session count
// and cadence. This prevents creating a cohort with only its first
// session scheduled when the user enters just the session 1 date.
// - Coaching (group) sessions only. Other session types are untouched (AC5).
// - Dates the user explicitly entered are preserved; only blanks fill (AC4).
// - Creation flow only. Only CreateCohortPage calls it, so the
//   edit-cohort flow never triggers it (AC6).
function autoFillCoachingDates(
  dates: (Date | null)[],
  sessionCount: number,
  frequency: SessionFrequency
): (Date | null)[] {
  const anchor = ensureDate(dates[0]);
  if (!anchor || sessionCount <= 1) return dates;

  const schedule = generateAnchoredSessionDates(
    anchor,
    sessionCount,
    frequency
  );
  return Array.from({ length: sessionCount }, (_, i) =>
    i === 0 ? anchor : (ensureDate(dates[i]) ?? schedule[i])
  );
}

export interface CohortDateRange {
  /** Group coaching dates after the auto-fill. Unchanged when the modality is off. */
  groupCoachingDates: (Date | null)[];
  /** Earliest session date (YYYY-MM-DD), or undefined when no date is set. */
  startDate: string | undefined;
  /** Latest session date (YYYY-MM-DD), or undefined when no date is set. */
  endDate: string | undefined;
}

/**
 * The cohort start and end dates the wizard sends on create. The wizard has
 * no start or end field: the range is the first and last session date.
 */
export function deriveCohortDateRange(
  values: Pick<
    FormValues,
    'selectedModalities' | 'sessionDates' | 'groupCoachingConfig'
  >
): CohortDateRange {
  const { selectedModalities, sessionDates, groupCoachingConfig } = values;

  let groupCoachingDates = sessionDates.group_coaching;
  if (selectedModalities.includes('group_coaching')) {
    groupCoachingDates = autoFillCoachingDates(
      sessionDates.group_coaching,
      groupCoachingConfig.sessionCount,
      groupCoachingConfig.frequency
    );
  }

  const allDates = [
    ...groupCoachingDates,
    ...sessionDates.one_on_one,
    ...sessionDates.workshop,
  ]
    .map(d => ensureDate(d))
    .filter((d): d is Date => d !== null)
    .sort((a, b) => a.getTime() - b.getTime());

  return {
    groupCoachingDates,
    startDate: allDates.length > 0 ? toISODateString(allDates[0]) : undefined,
    endDate:
      allDates.length > 0
        ? toISODateString(allDates[allDates.length - 1])
        : undefined,
  };
}

/**
 * MED-1350: program length in weeks from the derived range, or null while the
 * length is not known. The wizard has no cohort end date, so the length is
 * known only once the sessions span at least two distinct dates. With one
 * date the range is a single day, and the 1 week length it gives made the
 * Midpoint week auto-fill with 1, the same week as Intro.
 */
export function wizardProgramWeeks(
  values: Pick<
    FormValues,
    'selectedModalities' | 'sessionDates' | 'groupCoachingConfig'
  >
): number | null {
  const { startDate, endDate } = deriveCohortDateRange(values);
  if (!startDate || startDate === endDate) return null;
  return programWeeks(startDate, endDate);
}

/**
 * MED-1350: the check-in error of the wizard, or null. An empty week is
 * allowed until the program length is known (the API fills the default). A
 * typed week must still fit the length the API computes from the dates sent.
 */
export function wizardCheckinsError(
  checkins: CheckinSettingsForm,
  values: Pick<
    FormValues,
    'selectedModalities' | 'sessionDates' | 'groupCoachingConfig'
  >
): string | null {
  const { startDate, endDate } = deriveCohortDateRange(values);
  return (
    checkinsError(checkins, wizardProgramWeeks(values)) ??
    checkinWeekError(
      {
        intro_checkin_target_week: checkins.introEnabled
          ? checkins.introWeek
          : null,
        midpoint_checkin_target_week: checkins.midpointEnabled
          ? checkins.midpointWeek
          : null,
      },
      programWeeks(startDate, endDate)
    )
  );
}
